import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Steps, Card, Modal, Spin } from 'antd';
import axios from 'axios';
import StepEmail from './steps/StepEmail';
import StepPasswordDeclarant from './steps/StepPasswordDeclarant';
import StepAdhDetails from './steps/StepAdhDetails';

const { Step } = Steps;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Composant d'inscription pour les adhérents
 * Gère le workflow en slides adaptatif selon que l'utilisateur existe ou non
 */
const SignupUnified = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  // Données passées par Connexion.js
  const membershipCode = state?.message || state?.membershipCode;
  const companyName = state?.companyName;

  // States
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [userExists, setUserExists] = useState(false);
  const [emailChecked, setEmailChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companyPrefill, setCompanyPrefill] = useState(null);

  // Rediriger si pas de données
  if (!membershipCode) {
    navigate('/');
    return null;
  }

  // Gérer l'avancement au step suivant
  const handleNext = async (stepData) => {
    const updatedData = { ...formData, ...stepData };
    setFormData(updatedData);

    // Si c'est le step Email, stocker l'info userExists et récupérer les données company
    if (stepData.userExists !== undefined) {
      setUserExists(stepData.userExists);
      setEmailChecked(true);

      if (membershipCode) {
        try {
          const res = await axios.get(`${BACKEND_URL}/api/company/public-info`, {
            params: { membershipCode },
          });
          setCompanyPrefill(res.data);
        } catch {
          // En cas d'erreur, les champs seront simplement vides
        }
      }
    }

    setCurrentStep(currentStep + 1);
  };

  // Gérer le retour au step précédent
  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  // Soumission finale du formulaire
  const handleFinalSubmit = async (finalStepData) => {
    setLoading(true);
    setError(null);

    // Combiner toutes les données
    const completeData = {
      membershipCode,
      ...formData,
      ...finalStepData,
    };

    try {
      const response = await axios.post(`${BACKEND_URL}/api/register`, completeData);

      if (response.data.message === 'ok' && response.data.action === 'check_email') {
        setLoading(false); // Arrêter le spinner avant d'afficher la modal
        // Afficher modal de confirmation
        Modal.success({
          title: 'Inscription réussie !',
          content: (
            <div>
              <p>Un email de validation a été envoyé à <strong>{formData.email}</strong>.</p>
              <p>Veuillez cliquer sur le lien contenu dans cet email pour finaliser votre inscription.</p>
              <p><em>Le lien est valable pendant 24 heures.</em></p>
            </div>
          ),
          okText: 'Retour à l\'accueil',
          onOk: () => navigate('/'),
        });
      }
    } catch (err) {
      if (err.response && err.response.data) {
        const errorMessages = err.response.data.data || ['Erreur lors de l\'inscription'];
        setError(errorMessages.join(', '));
      } else {
        setError('Erreur de connexion au serveur');
      }
      setLoading(false);
    }
  };

  // Déterminer les steps à afficher
  const getSteps = () => {
    const steps = [
      {
        title: 'Email',
        component: (
          <StepEmail
            onNext={handleNext}
            membershipCode={membershipCode}
            companyName={companyName}
          />
        ),
      },
    ];

    // Slide 2 : Password + Déclarant (SEULEMENT si nouvel user)
    if (emailChecked && !userExists) {
      steps.push({
        title: 'Compte',
        component: (
          <StepPasswordDeclarant
            onNext={handleNext}
            onPrev={handlePrev}
          />
        ),
      });
    }

    // Slide 3 : Détails entreprise adhérente
    if (emailChecked) {
      steps.push({
        title: 'Entreprise adhérente',
        component: (
          <StepAdhDetails
            onNext={handleNext}
            onPrev={handlePrev}
            onSubmit={handleFinalSubmit}
            loading={loading}
            companyPrefill={companyPrefill}
          />
        ),
      });
    }

    return steps;
  };

  const steps = getSteps();
  const CurrentStepComponent = steps[currentStep]?.component;

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <Spin spinning={loading} fullscreen />
      <Card>
        <Steps current={currentStep} style={{ marginBottom: 32 }}>
          {steps.map((step, index) => (
            <Step key={index} title={step.title} />
          ))}
        </Steps>

        {error && (
          <div style={{ marginBottom: 16, padding: 16, background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 4 }}>
            <strong>Erreur :</strong> {error}
          </div>
        )}

        {CurrentStepComponent}
      </Card>
    </div>
  );
};

export default SignupUnified;
